import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  AssociateSiteVisitQueueRow,
  SiteVisitService,
  SiteVisitView,
} from '../../services/site-visit.service';
import { SchedulingQueueRowComponent } from '../../scheduling/scheduling-queue-row/scheduling-queue-row.component';
import {
  SchedulingQueueRowVm,
  buildQueueRowVm,
  pendingActionsFor,
  queueGroupOf,
  reconcileRow,
  rowFromDetailView,
} from '../../scheduling/scheduling-queue.util';
import { SiteVisitNegotiationComponent } from '../../site-visit/site-visit-negotiation/site-visit-negotiation.component';
import { isTerminalStatus } from '../../site-visit/site-visit-state.util';
import { createTestIdScope } from '../../shared/testid/testid.util';

/**
 * The (project, site) one queue read was started for.
 *
 * 🔴 Two jobs, and they are not the same job. `key` is what a later read is compared against, so a
 * re-emission that changed neither id does not refetch — and so an answer to a read the card has
 * already moved on from is discarded rather than rendered. `projectId` and `siteNumber` are the ids
 * that read is *about*: the fallback addresses the site the queue answered for, not whatever the card
 * happens to hold by the time the answer lands. Reading them off the component there would issue a
 * request for one site on behalf of another site's read.
 */
interface SiteVisitFetchScope {
  readonly key: string;
  readonly projectId: string;
  readonly siteNumber: number | null;
}

/**
 * The workspace's site-visit card — **the scheduling queue, scoped to this workspace's physical site.**
 *
 * <h2>What this used to do, and why it was wrong</h2>
 * It decided scheduled-vs-unscheduled from `proposal.siteVisit1..3` — the legacy per-product preferred
 * windows — and offered a three-date form whenever they were empty. The site-contact negotiation flow
 * writes a `SiteVisitSchedule` and never touches those fields, so a visit three states past the form
 * still rendered the form. The card was safe against the new flow (a submit came back superseded) but
 * not aware of it: a PA who submitted got a correct refusal, and a PA who merely looked got a wrong
 * render.
 *
 * 🔴 **Nothing here reads `proposal.siteVisit1/2/3` any more.** The fields still exist on the proposal
 * and the batch endpoint still exists; this surface simply stopped being a second, older account of
 * where a site visit stands. Reintroducing a read of them would recreate exactly the defect above.
 *
 * <h2>The card owns its own fetch, and both of them</h2>
 * The workspace passes `[projectId]` and `[siteNumber]` — ids it has already resolved — and nothing
 * else. Two reads live here rather than in the ~1100-line workspace component:
 *  - `getSiteVisitQueue()` on init and on any change of the two ids, filtered to this site. The queue
 *    is the read whose shape the shared row renders (`AssociateSiteVisitQueueRow`), which the
 *    site-scoped detail read is not.
 *  - `getSiteVisit(projectId, siteNumber)` when a row is opened — the same call the scheduling page
 *    makes, for the same reason: it is what heals a visit parked in `REJECTED_ALTERNATIVES_PROVIDED`
 *    awaiting a `SYSTEM` advance nothing else applies. Rendering the panel from the list would leave
 *    such a visit permanently stuck.
 *
 * Splitting the queue read up into the workspace and leaving the detail read here would spread one
 * data story across two components for no gain, so both are here.
 *
 * <h2>🔴 The completed-project fallback</h2>
 * The queue read excludes `SITE_VISIT_COMPLETED` and `CANCELLED` **in its query**
 * (`SiteVisitAssociateHandler.LIVE_QUEUE_STATUSES`), and that exclusion is deliberate: the queue is the
 * associate's cross-project work list and finished work is not work. `/project-history/:id` renders
 * this same workspace with `readOnly`, and its products are exactly those two states — so the queue
 * alone answers "nothing" for every completed project, and the confirmed day and site contact a
 * finished record used to show would vanish.
 *
 * So when the queue returns **no row for this project and site**, the card asks the site-scoped read
 * instead — `forSite` has no status filter and answers for a terminal visit — and renders the same row
 * from it via {@link rowFromDetailView}. The queue endpoint is not widened, and nothing here goes back
 * to reading `proposal.siteVisit1..3`: read-only and live render from one row builder, so a completed
 * visit reads identically on both.
 *
 * 🔴 Only on the **success-with-no-match** path. A queue read that failed also leaves no rows, and
 * falling back there would replace an honest "couldn't be loaded" with a maybe-empty answer. And
 * `advanceStuckVisit`, which `forSite` runs first, is a no-op on both terminal states —
 * `systemAdvanceFor` answers `null` for anything but `APPROVED`/`REJECTED_ALTERNATIVES_PROVIDED`, so
 * nothing is written and nothing is sent by a history page merely being opened.
 *
 * <h2>🔴 Filtered by project AND site number</h2>
 * The workspace is scoped to ONE physical site: `rebuildFromDetails` resolves the route's `:id` to a
 * single `SiteVm` and hands this card that site's products. The `appliesTo` chips above the rows
 * therefore name *this site's* requiring products, so a row belonging to a sibling site of the same
 * project would sit under a caption that is false about it. Project-only filtering is the right
 * predicate for a project-level surface and the wrong one here. `siteNumber` degrades to a
 * project-only filter when the workspace could not resolve one (the grouping util admits that case),
 * and the rows are sorted by `siteNumber` either way.
 *
 * <h2>What is NOT here</h2>
 * Every state's interaction is {@link SiteVisitNegotiationComponent}, projected into the opened row —
 * the same component the per-project site-visit area and the scheduling queue use. Nothing in this card
 * re-implements a state's form, and the row's own rendering belongs to
 * {@link SchedulingQueueRowComponent}: a visit must not read one way in the queue and another way here.
 */
@Component({
  standalone: true,
  selector: 'app-scheduling-card',
  imports: [SchedulingQueueRowComponent, SiteVisitNegotiationComponent],
  templateUrl: './scheduling-card.component.html',
  styleUrl: './scheduling-card.component.scss',
})
export class SchedulingCardComponent implements OnInit, OnChanges, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  // The physical site's full product collection (survey + reports). Still the source of the "requiring
  // set" (`site.siteVisitRequired`), which is what the chips name and what tells a site that needs no
  // visit apart from one whose visit simply has not been read yet. It is no longer the source of any
  // scheduling STATE. Typed loosely to match the rest of the workspace (siteDetail/site/proposal are
  // all `any`).
  @Input() products: any[] = [];

  /** The workspace's resolved project — `matchedProject.projectId`, the same id space the queue rows carry. */
  @Input() projectId: string | null = null;

  /** The workspace's resolved physical site. Null narrows the filter to the project alone — see the class comment. */
  @Input() siteNumber: number | null = null;

  // View-only mode (the /project-history/:id record). Passed straight through to the row, which then
  // renders neither the pending action nor View Details, and to the negotiation panel behind it.
  @Input() readOnly: boolean = false;

  /** This site's live visits, in the shared row's view-model shape. */
  rowVms: SchedulingQueueRowVm[] = [];

  loading = false;
  loadFailed = false;

  /** The visit whose row is expanded, by id. */
  openVisitId: string | null = null;

  /** The opened row's detail, fetched fresh. Never assembled from the list — see the class comment. */
  detail: SiteVisitView | null = null;
  detailLoading = false;
  detailFailed = false;

  /**
   * Whether the open row was opened by its *Reschedule* button — bound through to the panel's
   * `startRescheduleExpanded` so the named form is already on screen. The scheduling page carries the
   * same flag and the same three clearing points, deliberately: the two surfaces host the identical row
   * and must not disagree about what pressing a control on it does. See
   * `scheduling-page.component.ts`'s `rescheduleIntent` for the reasoning in full.
   *
   * 🔴 Nothing sets it on the read-only surface, and no `readOnly` test is written here to achieve
   * that. Two gates already stand in front of it: the row hides its whole control block behind
   * `!readOnly`, so there is no Reschedule button to press, and {@link open} refuses outright — which
   * is why {@link openForReschedule} reads the flag back off {@link isOpen} instead of asserting
   * `true`. A third check would be a fourth place for the answer to drift.
   */
  rescheduleIntent = false;

  /** The scope of the read the card is currently waiting on — see {@link SiteVisitFetchScope}. */
  private fetched: SiteVisitFetchScope | null = null;

  constructor(private siteVisitService: SiteVisitService) {}

  ngOnInit(): void {
    this.fetchIfNeeded();
  }

  /**
   * The workspace resolves its project and site asynchronously and re-emits on every `ProjectService`
   * merge, so the ids can arrive — or change, when the route `:id` moves to another site — after this
   * card is constructed. Keying the refetch on the two ids is what makes a late arrival fetch and a
   * mere re-emission not.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId'] || changes['siteNumber']) {
      this.fetchIfNeeded();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- the queue read ---------------------------------------------------------------------------

  private get fetchScope(): SiteVisitFetchScope | null {
    const projectId = this.projectId;
    if (!projectId) {
      return null;
    }
    const siteNumber = this.siteNumber ?? null;
    return { key: `${projectId}|${siteNumber ?? ''}`, projectId, siteNumber };
  }

  /**
   * 🔴 Is `scope` still the read this card is waiting on?
   *
   * Every handler below asks before it writes. The workspace component is REUSED across sites — the
   * two routes share one config, so following an ACTIVE notification from one site of a project to
   * another swaps the ids on a card that is never destroyed — and both reads here are then in flight
   * at once. The earlier one's answer is about a site the PA has already left: its rows filter to
   * nothing against the current ids and would blank a correctly-rendered card, and its failure would
   * put "couldn't be loaded" over a row that loaded fine. Neither refetches afterwards, because the
   * key the dedupe consults is already the current one.
   */
  private isCurrent(scope: SiteVisitFetchScope): boolean {
    return this.fetched?.key === scope.key;
  }

  private fetchIfNeeded(): void {
    const scope = this.fetchScope;
    if (!scope) {
      // No project resolved yet. Leave the card in its neutral state rather than reading the whole
      // queue and filtering it against `undefined`, which would match nothing and look like an answer.
      return;
    }
    if (scope.key === this.fetched?.key) {
      return;
    }
    this.fetched = scope;
    this.fetch(scope);
  }

  private fetch(scope: SiteVisitFetchScope): void {
    this.loading = true;
    this.loadFailed = false;
    this.close();
    this.siteVisitService
      .getSiteVisitQueue()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (!this.isCurrent(scope)) {
            return;
          }
          if (response?.status === 'FAILURE') {
            this.loading = false;
            this.loadFailed = true;
            this.build([]);
            return;
          }
          this.build(response?.data ?? []);
          if (this.hasRows) {
            this.loading = false;
            return;
          }
          // Nothing live for this site. It may be a completed or cancelled visit, which the queue
          // excludes server-side — ask the site-scoped read before settling on an empty card. `loading`
          // deliberately stays true across the second read, so a history page does not flash "no site
          // visit to display" on its way to rendering one.
          this.fallbackToDetail(scope);
        },
        error: () => {
          if (!this.isCurrent(scope)) {
            return;
          }
          this.loading = false;
          this.loadFailed = true;
          this.build([]);
        },
      });
  }

  /** Narrow the PA's whole queue to this site, then derive the shared row view-model for what is left. */
  private build(rows: AssociateSiteVisitQueueRow[]): void {
    const scope = createTestIdScope();
    this.rowVms = (rows ?? [])
      .filter((row) => !!row && this.belongsHere(row))
      .sort((a, b) => (a.siteNumber ?? 0) - (b.siteNumber ?? 0))
      .map((row) => buildQueueRowVm(row, scope));
  }

  /**
   * The completed-project fallback — see the class comment.
   *
   * Needs a `siteNumber`: the site-scoped read is addressed by one, and a workspace that resolved none
   * has no URL to build. That case keeps the empty card, which is what it had before. A `data: null`
   * answer is the documented "this site has no negotiation" and keeps it too — the card must not invent
   * a third empty state for a case the existing one already describes.
   *
   * 🔴 Every id here comes off `scope` — the (project, site) the QUEUE read answered for — and never
   * off the component. The workspace re-emits its ids when the route `:id` moves to another site, and
   * reading them here would address this second request to the site the card moved to on behalf of a
   * read about the site it left. Its own guard would then discard the answer, so the request is pure
   * waste in the best case and a request the PA never asked for in every case.
   *
   * The scope check is the first thing this method does, so nothing below writes state on behalf of a
   * read the card has moved on from. The queue handler that calls this has already made the same
   * check — this is the method standing on its own rather than a second live case.
   */
  private fallbackToDetail(scope: SiteVisitFetchScope): void {
    if (!this.isCurrent(scope)) {
      return;
    }
    const { projectId, siteNumber } = scope;
    if (siteNumber === null) {
      this.loading = false;
      return;
    }
    // No product on this site requires a visit, so there is nothing for a terminal negotiation to be
    // the history of — `hasNoVisitRequired` already has the right answer and it is the one this card
    // gave before the fallback existed. Skipping the read also keeps a non-requiring site from
    // issuing a request on every workspace load.
    //
    // This deliberately diverges from the queue path, which does render a row on a non-requiring site.
    // The two are not symmetric: a live negotiation is worth surfacing wherever it exists, but a
    // completed one on a site that never needed a visit is a curiosity, not history the PA is looking
    // for. Chosen 2026-08-28; flip this guard, not the queue path, if that judgment changes.
    if (this.requiringCount === 0) {
      this.loading = false;
      return;
    }
    this.siteVisitService
      .getSiteVisit(projectId, siteNumber)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (!this.isCurrent(scope)) {
            return;
          }
          this.loading = false;
          if (response?.status === 'FAILURE') {
            // A refused read is not an answer. Say so, exactly as the queue read does, rather than
            // letting it read as "this site has no negotiation".
            this.loadFailed = true;
            return;
          }
          const view = response?.data ?? null;
          if (!view) {
            return;
          }
          this.rowVms = [
            buildQueueRowVm(
              rowFromDetailView(view, projectId, siteNumber),
              createTestIdScope()
            ),
          ];
        },
        error: () => {
          if (!this.isCurrent(scope)) {
            return;
          }
          this.loading = false;
          this.loadFailed = true;
        },
      });
  }

  /**
   * 🔴 Project AND site, because this card is site-scoped and its chips say so. See the class comment.
   * A workspace that resolved no `siteNumber` falls back to the project alone rather than matching
   * nothing.
   */
  private belongsHere(row: AssociateSiteVisitQueueRow): boolean {
    if (!this.projectId || row.projectId !== this.projectId) {
      return false;
    }
    if (this.siteNumber === null || this.siteNumber === undefined) {
      return true;
    }
    return row.siteNumber === this.siteNumber;
  }

  // --- requiring set + chips --------------------------------------------------------------------

  get requiringProducts(): any[] {
    return (this.products ?? []).filter(
      (product) => product?.site?.siteVisitRequired
    );
  }

  get requiringCount(): number {
    return this.requiringProducts.length;
  }

  // Chip row — which products the one visit applies to. Unchanged: the workspace is organized around
  // products and the link between a visit and the products it covers is worth keeping, even though the
  // visit's own state no longer comes from them.
  get appliesTo(): { title: string; testId: string }[] {
    return this.requiringProducts.map((product) => ({
      title: product.title ?? 'Product',
      testId: product.testId ?? '',
    }));
  }

  get hasRows(): boolean {
    return this.rowVms.length > 0;
  }

  /**
   * No visit to show, and no product asked for one — the honest terminal note, told apart from "a visit
   * is expected but the queue returned none" so the two do not share one misleading sentence.
   */
  get notRequired(): boolean {
    return !this.hasRows && this.requiringCount === 0;
  }

  // --- rows -------------------------------------------------------------------------------------

  trackByVisitId(_index: number, vm: SchedulingQueueRowVm): string {
    return vm.row.id;
  }

  isOpen(vm: SchedulingQueueRowVm): boolean {
    return this.openVisitId === vm.row.id;
  }

  /**
   * Expand a row, or collapse it if it is already the open one.
   *
   * 🔴 Expanding always fetches the detail, exactly as the scheduling page does — that read is what
   * advances a visit parked awaiting a `SYSTEM` step, and nothing else on this surface does it.
   */
  open(vm: SchedulingQueueRowVm): void {
    if (this.readOnly) {
      return;
    }
    if (this.isOpen(vm)) {
      this.close();
      return;
    }
    // A generic open — View Details, or the pending action. Cleared here as well as in `close()`
    // because the PA can move straight from one open row to another.
    this.rescheduleIntent = false;
    this.openVisitId = vm.row.id;
    this.detail = null;
    this.detailFailed = false;

    const projectId = vm.row.projectId;
    const siteNumber = vm.row.siteNumber;
    if (!projectId || siteNumber === null || siteNumber === undefined) {
      this.detailFailed = true;
      return;
    }

    this.detailLoading = true;
    this.siteVisitService
      .getSiteVisit(projectId, siteNumber)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Another row was opened while this was in flight. `detail` is a singleton, so writing to it
          // now would render this visit's negotiation under the other row's header.
          if (this.openVisitId !== vm.row.id) {
            return;
          }
          this.detailLoading = false;
          const view = response?.status === 'FAILURE' ? null : response?.data ?? null;
          if (!view) {
            this.detailFailed = true;
            return;
          }
          this.detail = view;
          // 🔴 A READ. Whatever this answer says about the visit, the associate has not done anything
          // to it — see {@link commit}.
          this.commit(vm.row.id, view, false);
        },
        error: () => {
          if (this.openVisitId !== vm.row.id) {
            return;
          }
          this.detailLoading = false;
          this.detailFailed = true;
        },
      });
  }

  /**
   * Open the row AND record that the PA asked for the reschedule form by name — the workspace's half of
   * the same wiring the scheduling page carries, so the row behaves identically on both surfaces.
   *
   * 🔴 The flag is read back off {@link isOpen} rather than set to `true`, which makes both of
   * {@link open}'s early exits correct with no branch here: the read-only card never opens, so intent
   * stays false; and handed the already-open row `open` toggles it SHUT, where asserting intent would
   * strand a stale `true` for whatever opens next.
   */
  openForReschedule(vm: SchedulingQueueRowVm): void {
    this.open(vm);
    this.rescheduleIntent = this.isOpen(vm);
  }

  close(): void {
    this.openVisitId = null;
    this.detail = null;
    this.detailLoading = false;
    this.detailFailed = false;
    this.rescheduleIntent = false;
  }

  /**
   * The negotiation moved under the PA's own hand — same fold-back path as the detail read, and the
   * ONLY one that may settle a row. See {@link commit}.
   */
  onVisitChange(view: SiteVisitView): void {
    this.detail = view;
    // The intent is spent with the open it caused. A commit can re-derive the row and rebuild its
    // projected panel without `open` or `close` running, and the panel has just emptied the reschedule
    // fields — leaving this set would re-expand a blank form as if it were mid-edit.
    this.rescheduleIntent = false;
    if (this.openVisitId) {
      this.commit(this.openVisitId, view, true);
    }
  }

  /**
   * Adopt a freshly-read negotiation onto its row in place, and re-derive what depends on it.
   *
   * In place rather than refetching, for the reason the scheduling page keeps the same behavior:
   * rebuilding the list closes the row the PA is working in. A visit driven to a terminal state keeps
   * its place, marked settled, so what the PA just did stays legible.
   *
   * 🔴 **`settled` renders "Updated — this visit leaves the queue" — a claim about what the ASSOCIATE
   * just did — so it takes TWO conditions, and they answer different questions.**
   *
   * `fromTransition` answers *did the associate do anything at all?* Only {@link onVisitChange} passes
   * `true`: it is the negotiation panel reporting a move the PA made. {@link open} passes `false` — it
   * is a read, and a read must never settle a row. `wasTerminal` is the QUEUE's snapshot, taken when
   * the list was fetched and possibly minutes stale, so a visit somebody else completed or cancelled in
   * the meantime arrives at `commit` looking exactly like one the PA just drove terminal. Without this
   * condition, a PA who merely clicked View Details is congratulated on a departure they had no part
   * in.
   *
   * `!wasTerminal` answers *was there a transition to announce?* A row that arrived through the
   * completed-project fallback is already terminal, so testing the state alone would put the banner
   * over a visit nobody touched — and the promise would be false twice, since the next load renders the
   * same row through the same fallback.
   *
   * Both are needed. Neither implies the other: a read can arrive on a genuine transition, and a real
   * transition can arrive on an already-terminal row. This is the one place this card can differ from
   * the scheduling page, whose rows are live by construction, are only ever committed from a surface
   * the PA is acting on, and can only reach a terminal state by being driven there.
   */
  private commit(
    visitId: string,
    view: SiteVisitView,
    fromTransition: boolean
  ): void {
    const vm = this.rowVms.find((candidate) => candidate.row.id === visitId);
    if (!vm) {
      return;
    }
    const wasTerminal = isTerminalStatus(vm.row.status);
    vm.row = reconcileRow(vm.row, view);
    if (isTerminalStatus(vm.row.status)) {
      vm.settled = fromTransition && !wasTerminal;
      vm.pending = [];
      return;
    }
    vm.settled = false;
    vm.pending = pendingActionsFor(vm.row);
    vm.group = queueGroupOf(vm.row);
  }
}
